import { type ReactNode } from 'react';

import { cx } from '../lib/cx';

export interface EmptyStateProps {
  /** Icon node (decorative), shown in the soft tile. */
  icon?: ReactNode;
  title: string;
  body: ReactNode;
  /** Primary/secondary actions, rendered in a row. */
  actions?: ReactNode;
  /** Extra content below the actions (a steps strip or stat row). */
  children?: ReactNode;
  className?: string;
}

/** The canonical empty-state block (design-reference §6.14). */
export function EmptyState({ icon, title, body, actions, children, className }: EmptyStateProps): React.JSX.Element {
  return (
    <div className={cx('ds-empty', className)}>
      {icon !== undefined ? (
        <div className="ds-empty__icon" aria-hidden="true">
          {icon}
        </div>
      ) : null}
      <h2 className="ds-empty__title">{title}</h2>
      <p className="ds-empty__body">{body}</p>
      {actions !== undefined ? <div className="ds-empty__actions">{actions}</div> : null}
      {children}
    </div>
  );
}

export interface ErrorStateProps {
  title: string;
  body: ReactNode;
  /** Optional mono trace id line. */
  traceId?: string;
  actions?: ReactNode;
  className?: string;
}

/** The error-state block: `!` tile, message, optional trace id, retry action. */
export function ErrorState({ title, body, traceId, actions, className }: ErrorStateProps): React.JSX.Element {
  return (
    <div className={cx('ds-error', className)} role="alert">
      <div className="ds-error__icon" aria-hidden="true">
        !
      </div>
      <h2 className="ds-error__title">{title}</h2>
      <p className="ds-error__body">{body}</p>
      {traceId !== undefined ? <p className="ds-error__trace">trace_id: {traceId}</p> : null}
      {actions !== undefined ? <div className="ds-error__actions">{actions}</div> : null}
    </div>
  );
}
